import { useState } from 'react';
import { ASC, DESC, VALUATION_DATE_KEY } from 'common/constants/analyst-dashboards';
import { AttioSortDirection, AttioValuationFilter } from 'dashboard409a/common/constants/analyst-dashboard';
import { GenericWorkboard, useNoteHandler } from 'dashboard409a/components/ValuationsTable/common';
import { ValuationsTableBulkData } from 'dashboard409a/components/ValuationsTable/ValuationsTableBulkData';
import { useValuationsWithBulkData } from 'dashboard409a/hooks/useValuationsWithBulkData';
import { CreateNoteRequest } from 'dashboard409a/services/attio.types';
import { getInitialSortFromStorage } from 'dashboard409a/utilities';

const VALUATIONS_SORT_KEY = 'valuationsSort';

interface WorkboardDataTableProps {
  filters: AttioValuationFilter;
  orderBy?: string;
  order?: AttioSortDirection;
}

const WorkboardDataTable = ({
  filters,
  orderBy: initialOrderBy = VALUATION_DATE_KEY,
  order: initialOrder = DESC,
}: WorkboardDataTableProps) => {
  // Initialize state from localStorage if available
  const initialSort = getInitialSortFromStorage(VALUATIONS_SORT_KEY, initialOrderBy, initialOrder);
  const [orderBy, setOrderBy] = useState(initialSort.sortBy);
  const [order, setOrder] = useState<AttioSortDirection>(initialSort.sortOrder);

  const {
    rows,
    isLoading,
    updateValuationStatus,
    updateValuationNotes,
    updateValuationEngagementManager,
  } = useValuationsWithBulkData({
    filters,
    orderBy,
    order,
  });

  // Use the generic note handler
  const { isNoteLoading, noteError, handleNoteSave: handleNoteSaveGeneric } = useNoteHandler({
    onSuccess: (id, noteContent, createdNote) => {
      // If a note was just created, use the attio_valuation_id from the result
      if (createdNote?.attio_valuation_id) {
        const valuationId = createdNote.attio_valuation_id;
        // Update the note with the real ID from the created note
        const noteId = createdNote.id;
        updateValuationNotes(valuationId, noteContent, noteId);
      } else {
        // For updates, use the id parameter (which is the valuationId)
        updateValuationNotes(id as string, noteContent);
      }
    },
  });


  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? DESC : ASC);
    setOrderBy(property);
  };

  const handleNoteSave = async (valuationId: string, noteContent: string, currentNote?: string) => {
    const request: CreateNoteRequest = {
      content: noteContent,
      attio_valuation_id: valuationId,
      reference_type: 'at',
    };

    // Determine if we're updating an existing note or creating a new one
    const currentValuation = rows.find(r => r.record_id === valuationId);
    const hasExistingNote = Boolean(
      (currentNote && currentNote.trim() !== '' && currentNote !== '-') ||
      (currentValuation?.note?.content && currentValuation.note.content.trim() !== '')
    );

    let existingNoteId: string | undefined;
    
    if (hasExistingNote) {
      // Check if the note has a valid ID (not 0, undefined, or null)
      const noteId = currentValuation?.note?.id;
      if (noteId && noteId !== 0) {
        existingNoteId = noteId.toString();
      } else {
        // If note ID is invalid (0 or undefined), treat as new note
        existingNoteId = undefined;
      }
    }

    await handleNoteSaveGeneric(valuationId, noteContent, currentNote, request, hasExistingNote, existingNoteId);
  };

  return (
    <GenericWorkboard>
      <ValuationsTableBulkData
        isLoading={isLoading}
        rows={rows}
        onStatusUpdate={updateValuationStatus}
        onNoteUpdate={updateValuationNotes}
        onNoteSave={handleNoteSave}
        orderBy={orderBy}
        order={order}
        onRequestSort={handleRequestSort}
        isNoteLoading={isNoteLoading}
        noteError={noteError}
        onEngagementManagerUpdate={updateValuationEngagementManager}
      />
    </GenericWorkboard>
  );
};

export default WorkboardDataTable;
